/**
 * Shared map-provider infrastructure for the two PPPoE map surfaces
 * (site-wide PppoeMap.js, the coordinate picker on PppoeAccountDetail.js).
 *
 * CartoDB's anonymous free basemap tiles started requiring a signed-up API
 * key (confirmed live — an unauthenticated request now returns a tiny
 * placeholder PNG reading "API KEY REQUIRED" instead of a real tile), so the
 * OSM path here points at the standard tile.openstreetmap.org servers,
 * which remain free with no key. Google Maps is offered as an alternative
 * once an admin saves and verifies a key on the Settings page — with no key
 * saved (or a key that fails verification), the system stays on OSM and the
 * Google Maps toggle doesn't appear at all.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

export const OSM_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
export const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const STORAGE_KEY = "nhp_map_provider";

export function useMapProvider() {
  const [provider, setProviderState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "google" ? "google" : "osm";
    } catch {
      return "osm";
    }
  });
  const setProvider = (next) => {
    setProviderState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  };
  return [provider, setProvider];
}

/** Fetches the tenant's saved Google Maps settings once on mount. Callers
 * should only offer the Google Maps option once `loaded && valid` — an
 * unverified or missing key means Google Maps stays hidden entirely. */
export function useGoogleMapsSettings() {
  const [state, setState] = useState({ loaded: false, apiKey: "", valid: false });
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/settings/maps")
      .then(({ data }) => {
        if (cancelled) return;
        setState({ loaded: true, apiKey: data.google_maps_api_key || "", valid: !!data.google_maps_api_key_valid });
      })
      .catch(() => { if (!cancelled) setState({ loaded: true, apiKey: "", valid: false }); });
    return () => { cancelled = true; };
  }, []);
  return state;
}

export function MapProviderToggle({ provider, onChange, className = "" }) {
  return (
    <div className={`inline-flex border border-nhp-border shrink-0 ${className}`}>
      {[["osm", "OpenStreetMap"], ["google", "Google Maps"]].map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`px-2.5 h-8 font-mono text-[10px] uppercase tracking-[0.1em] ${
            provider === key
              ? "bg-nhp-primary text-nhp-on-primary"
              : "text-nhp-muted hover:text-nhp-text bg-nhp-surface"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

let googleLoadPromise = null;
let googleLoadedKey = null;

/** Loads the real Google Maps JS API into the main window for actual map
 * rendering. Wires `gm_authFailure` — Google's documented mechanism for
 * reporting an invalid/restricted/unbilled key at runtime — so a key that
 * breaks *after* passing the Settings-page check still fails gracefully
 * here instead of silently showing a blank map. */
export function loadGoogleMaps(apiKey) {
  if (!apiKey) return Promise.reject(new Error("NO_KEY"));
  if (window.google?.maps && googleLoadedKey === apiKey) return Promise.resolve(window.google.maps);
  if (googleLoadPromise && googleLoadedKey === apiKey) return googleLoadPromise;
  googleLoadedKey = apiKey;
  googleLoadPromise = new Promise((resolve, reject) => {
    const fail = () => { googleLoadPromise = null; reject(new Error("LOAD_FAILED")); };
    window.gm_authFailure = fail;
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return googleLoadPromise;
}

/** Used by the Settings page to verify a key before saving it. Loads the
 * API inside a throwaway iframe (its own isolated `window`) so testing one
 * key can't be polluted by an earlier attempt at a different key still
 * being cached in the main window, and so it never touches the real
 * `loadGoogleMaps()` cache used for actual map rendering elsewhere. */
export function testGoogleMapsKey(apiKey) {
  return new Promise((resolve) => {
    if (!apiKey) { resolve(false); return; }
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      iframe.remove();
      resolve(ok);
    };
    const iwin = iframe.contentWindow;
    iwin.__nhpMapsTestDone = () => finish(true);
    iwin.gm_authFailure = () => finish(false);
    const script = iwin.document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=__nhpMapsTestDone`;
    script.onerror = () => finish(false);
    iwin.document.head.appendChild(script);
    setTimeout(() => finish(false), 6000);
  });
}

export function GoogleMapUnavailable({ height, status }) {
  return (
    <div style={{ height }} className="border border-nhp-border flex items-center justify-center text-center p-4">
      {status === "no_key" ? (
        <span className="text-xs text-nhp-muted font-mono leading-relaxed">
          Google Maps isn't configured.<br />Add and verify a key under Settings → Maps.
        </span>
      ) : (
        <span className="text-xs text-nhp-critical font-mono">Failed to load Google Maps.</span>
      )}
    </div>
  );
}
